import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin, Star } from '@phosphor-icons/react';
import type { SearchResultCard } from '@pms/shared';
import { useFeaturedStays } from '../../hooks/useFeaturedStays';
import { formatPrice, initials } from '../../lib/format';
import { Reveal } from '../../lib/motion';

/**
 * Curated hotels showcase — editorial, not a uniform grid.
 *
 * A large feature hotel spans the top, then a varied arrangement of cards below.
 * Every hotel links to /hotel/:id; CTAs are "View hotel" / "Enquire" — never Book.
 *
 * Curated data (GET /v1/public/featured via useFeaturedStays): super-admin
 * picks which approved hotels appear and in what order, with one Editor's Pick
 * highlighted as the banner. Demo / hidden / regressed properties never appear
 * (enforced server-side). Degrades gracefully — skeleton while loading, a calm
 * empty state when nothing is curated yet (or the API is unreachable).
 */
export function CuratedHotels() {
  const { data, isPending, isError } = useFeaturedStays();
  // Editor's Pick fronts the banner; otherwise the first curated item (parity
  // with the previous stays[0] behaviour). The grid is the remainder.
  const feature = data?.editorsPick ?? data?.items[0];
  const rest = (data?.items ?? []).filter((s) => s.id !== feature?.id);

  return (
    <section aria-labelledby="curated-h2" className="bg-cream">
      <div className="px-6 py-20 sm:px-10 lg:px-16 lg:py-28 2xl:px-24">
        <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h2
              id="curated-h2"
              className="text-balance font-serif text-[2.4rem] font-black leading-[0.98] tracking-tight text-ink sm:text-5xl xl:whitespace-nowrap"
            >
              Hotels worth crossing a city for.
            </h2>
          </div>
          {feature && (
            <Link
              to="/search"
              className="group inline-flex w-fit items-center gap-1.5 border-b-2 border-clay pb-1 font-semibold text-clay transition hover:gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
            >
              See all hotels
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          )}
        </header>

        {isPending ? (
          <CuratedSkeleton />
        ) : feature ? (
          <>
            {/* Feature hotel — wide editorial banner */}
            <Reveal>
              <FeatureHotel stay={feature} />
            </Reveal>

            {/* Uniform grid — equal-height cards, one consistent image ratio */}
            {rest.length > 0 && (
              <div className="mt-8 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((stay, i) => (
                  <Reveal key={stay.id} delay={(i % 3) * 110} className="h-full">
                    <HotelCard stay={stay} />
                  </Reveal>
                ))}
              </div>
            )}
          </>
        ) : (
          <CuratedEmpty error={isError} />
        )}
      </div>
    </section>
  );
}

/** Loading placeholder — mirrors the feature-banner + 3-card rhythm. */
function CuratedSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="mt-12 aspect-[16/10] w-full animate-pulse rounded-[2rem] bg-sand sm:aspect-[21/9]" />
      <div className="mt-8 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="overflow-hidden rounded-[1.5rem] border border-sand bg-white">
            <div className="aspect-[4/3] w-full animate-pulse bg-sand" />
            <div className="space-y-3 p-5">
              <div className="h-5 w-2/3 animate-pulse rounded bg-sand" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-sand" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-sand" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shown when no public hotels exist yet (or the API can't be reached). Keeps
 *  the homepage composed rather than collapsing the section. */
function CuratedEmpty({ error }: { error: boolean }) {
  return (
    <div className="mt-12 flex flex-col items-center rounded-[2rem] border border-dashed border-clay/40 bg-white/60 px-6 py-16 text-center">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-clay-light text-clay">
        <Star weight="fill" className="h-6 w-6" aria-hidden="true" />
      </span>
      <h3 className="mt-5 font-serif text-2xl font-semibold text-ink">
        New stays, arriving soon
      </h3>
      <p className="mt-2 max-w-md text-ink-muted">
        {error
          ? "We couldn't load our hotels just now — please check back in a moment."
          : "We're hand-picking the next set of hotels worth crossing a city for. Check back shortly."}
      </p>
    </div>
  );
}

function FeatureHotel({ stay }: { stay: SearchResultCard }) {
  return (
    <Link
      to={`/hotel/${stay.id}`}
      className="group relative mt-12 block overflow-hidden rounded-[2rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-4 focus-visible:ring-offset-cream"
    >
      <div className="relative aspect-[16/10] sm:aspect-[21/9]">
        <img
          src={stay.imageUrl}
          alt={`${stay.name} — ${stay.location}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" aria-hidden="true" />
      </div>

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 p-6 text-cream sm:flex-row sm:items-end sm:justify-between sm:p-10">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-clay px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
            Editor's pick
          </span>
          <h3 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">{stay.name}</h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-cream/80">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {stay.location}
          </p>
        </div>
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5 text-cream">
            <Star weight="fill" className="h-4 w-4 text-clay" aria-hidden="true" />
            <span className="font-semibold">{stay.rating.toFixed(1)}</span>
            <span className="text-cream/70">({stay.reviewCount})</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-cream px-5 py-2.5 font-semibold text-ink transition group-hover:bg-clay group-hover:text-white">
            View hotel
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function HotelCard({ stay, tall = false }: { stay: SearchResultCard; tall?: boolean }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-sand bg-white transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-28px_rgba(28,26,23,0.55)]">
      <Link
        to={`/hotel/${stay.id}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-inset"
        aria-label={`View ${stay.name}, ${stay.location}`}
      >
        <div className={`relative overflow-hidden bg-clay-light ${tall ? 'aspect-[4/5]' : 'aspect-[4/3]'}`}>
          <HotelImage stay={stay} />
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-cream/95 px-2.5 py-1 text-sm font-semibold text-ink backdrop-blur">
            <Star weight="fill" className="h-3.5 w-3.5 text-clay" aria-hidden="true" />
            {stay.rating.toFixed(1)}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-xl font-semibold text-ink">
          <Link to={`/hotel/${stay.id}`} className="focus-visible:outline-none hover:text-clay">
            {stay.name}
          </Link>
        </h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-muted">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          {stay.location}
        </p>

        <div className="mt-auto flex items-end justify-between border-t border-sand pt-5">
          <p className="text-ink">
            <span className="text-xs uppercase tracking-wide text-ink-muted">from </span>
            <span className="font-serif text-lg font-semibold">
              {formatPrice(stay.pricePerNight, stay.currency)}
            </span>
            <span className="text-sm text-ink-muted"> / night</span>
          </p>
          <Link
            to={`/hotel/${stay.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-clay px-4 py-1.5 text-sm font-semibold text-clay transition hover:bg-clay hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          >
            Book now
          </Link>
        </div>
      </div>
    </article>
  );
}

function HotelImage({ stay }: { stay: SearchResultCard }) {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <div className="flex h-full w-full items-center justify-center font-serif text-3xl font-semibold text-clay">
        {initials(stay.name)}
      </div>
    );
  }
  return (
    <img
      src={stay.imageUrl}
      alt={`${stay.name}, ${stay.location}`}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      onError={() => setImgError(true)}
    />
  );
}
